import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axiosInstance";
import { useTranslation } from "react-i18next";
import Tab from "apollo-react/components/Tab";
import Tabs from "apollo-react/components/Tabs";
import Badge from "apollo-react/components/Badge";

import Card from "apollo-react/components/Card";
import CardContent from "apollo-react/components/CardContent";
import CardHeader from "apollo-react/components/CardHeader";
import Grid from "apollo-react/components/Grid";
import { t } from "i18next";
import ReviewsListCards from "../../common/Reviews_Cards/list";

const Approvals = ({ persona }) => {
  const [t] = useTranslation();
  const [activeTab, setActiveTab] = useState(() => {
    const storedTab = localStorage.getItem("approvals_activeTab");
    return storedTab !== null ? parseInt(storedTab, 10) : 0;
  });
  const [newRequests, setNewRequests] = useState([]);
  const [sentBackRequestsProgress, setSentBackRequestsProgress] = useState([]);
  const [inProgressRequestsProgress, setInProgressRequestsProgress] = useState([]);
  const [approvedRequestsProgress, setApprovedRequestsProgress] = useState([]);
  const [rejectedRequestsProgress, setRejectedRequestsProgress] = useState([]);
  const [committeeRequestProgress, setCommitteeRequestProgress] = useState([]);
  const [isCoreValidator, setIsCoreValidator] = useState(false);
  // const [selectedRows, setSelectedRows] = useState([]);

  const fetchRequests = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/api/requests-progress/approvals/current`,
        { withCredentials: true }
      );
      setNewRequests(response?.data.newRequests || []);
      setSentBackRequestsProgress(response?.data.sentBackRequestsProgress || []);
      setInProgressRequestsProgress(response?.data.inProgressRequestsProgress || []);
      setApprovedRequestsProgress(response?.data.approvedRequestsProgress || []);
      setRejectedRequestsProgress(response?.data.rejectedRequestsProgress || []);
      setCommitteeRequestProgress(response?.data.committeeRequestProgress || []);
      setIsCoreValidator(response?.data.isCoreValidator || false);
    } catch (err) {
      console.error("Error fetching requests:", err);
    }
  };
  useEffect(() => {
    fetchRequests();
  }, []);

  // Reset activeTab when isCoreValidator changes or component mounts
  useEffect(() => {
    const storedTab = localStorage.getItem("approvals_activeTab");
    const tabIndex = storedTab !== null ? parseInt(storedTab, 10) : 0;
    const maxIndex = isCoreValidator ? 5 : 4;
    setActiveTab(Math.min(tabIndex, maxIndex));
  }, [isCoreValidator]);

  const handleTabChange = (event, value) => {
    const maxIndex = isCoreValidator ? 5 : 4; // Core validator has 6 tabs (0-5), regular user has 5 tabs (0-4)
    const safeValue = Math.min(value, maxIndex);
    localStorage.setItem("approvals_activeTab", safeValue);
    setActiveTab(safeValue);
  };

  const handleComplete = async () => {
    await fetchRequests();
  };
  const getCurrentData = () => {
    if (!isCoreValidator) {
      // Regular users: Assigned, Sent Back, In Progress, Approved, Rejected
      switch (activeTab) {
        case 0:
          return newRequests; // Assigned
        case 1:
          return sentBackRequestsProgress; // Sent Back
        case 2:
          return inProgressRequestsProgress; // In Progress
        case 3:
          return approvedRequestsProgress; // Approved
        case 4:
          return rejectedRequestsProgress; // Rejected
        default:
          return newRequests;
      }
    }

    // Core validator users: Assigned, Sent Back, In Progress, Approved, Rejected, Assessment Reviews
    switch (activeTab) {
      case 0:
        return newRequests; // Assigned
      case 1:
        return sentBackRequestsProgress; // Sent Back
      case 2:
        return inProgressRequestsProgress; // In Progress
      case 3:
        return approvedRequestsProgress; // Approved
      case 4:
        return rejectedRequestsProgress; // Rejected
      case 5:
        return committeeRequestProgress; // Assessment Reviews
      default:
        return newRequests;
    }
  };

  return (
    <div>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        truncate
        style={{ marginBottom: "30px" }}
        shape="box"
      >
        <Tab
          label={
            <>
              {t("Assigned")}
              <Badge badgeContent={newRequests?.length} />
            </>
          }
        />
        <Tab
          label={
            <>
              {t("Sent Back")}
              <Badge badgeContent={sentBackRequestsProgress?.length} />
            </>
          }
        />
        <Tab
          label={
            <>
              {t("In Progress")}
              <Badge badgeContent={inProgressRequestsProgress?.length} />
            </>
          }
        />
        <Tab
          label={
            <>
              {t("Approved")}
              <Badge badgeContent={approvedRequestsProgress?.length} />
            </>
          }
        />
        <Tab
          label={
            <>
              {t("Rejected")}
              <Badge badgeContent={rejectedRequestsProgress?.length} />
            </>
          }
        />
        {isCoreValidator && (
          <Tab
            label={
              <>
                {t("Assessment Review")}
                <Badge badgeContent={committeeRequestProgress?.length} />
              </>
            }
          />
        )}
      </Tabs>

      <ReviewsListCards
        route={`/admin/requests`}
        requestsProgresses={getCurrentData()}
        onComplete={handleComplete}
        isCoreValidator={isCoreValidator}
        persona="admin"
        currentTab={activeTab}
      />
    </div>
  );
};

export default Approvals;

export const ApprovalsCards = ({ data, navigate, persona, activeTab  }) => {
  return (
    <Grid container spacing={2}>
      {data?.map((request) => (
        <Grid item xs={12} sm={6} md={4} key={request.id}>
          <Card
            style={{
              // height: "300px",
              // display: "flex",
              // flexDirection: "column",
              // justifyContent: "center",
              // alignItems: "center",
              cursor: "pointer",
            }}
            interactive
            onClick={() =>
              navigate(`/${persona}/requests/${request.id}`, {
                state: { tabIndex: activeTab }, // pass current tab
              })
            }
          >
            <CardHeader
              title={
                <>
                  {request?.request?.generated_id}
                  <br />
                  {request?.Stage?.name}
                </>
              }
            />

            <CardContent style={{ flex: 1, paddingBottom: "16px" }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  {t("Request Number")}:
                </Grid>
                <Grid item xs={6}>
                  {request?.id}
                </Grid>

                <Grid item xs={6}>
                  {t("Submitted Date")}:
                </Grid>
                <Grid item xs={6}>
                  {new Date(request.createdAt).toLocaleDateString('en-GB')}
                </Grid>

                <Grid item xs={6}>
                  {t("Last Action Date")}:
                </Grid>
                <Grid item xs={6}>
                  {new Date(request.updatedAt).toLocaleDateString('en-GB')}
                </Grid>

                <Grid item xs={6}>
                  {t("SLA to Review")}:
                </Grid>
                <Grid item xs={6}>
                  {request.slaToReview || "N/A"}
                </Grid>

                <Grid item xs={6}>
                  {t("Status")}:
                </Grid>
                <Grid item xs={6}>
                  {request.status}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};
